import React from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { COLORS } from '../../utils/theme';

const AnalyticsView = ({ data }) => {
  // Process yearly data
  const yearlyData = React.useMemo(() => {
    const yearCounts = data.reduce((acc, item) => {
      const year = item.year;
      if (year) {
        acc[year] = (acc[year] || 0) + 1;
      }
      return acc;
    }, {});

    const years = Object.keys(yearCounts).map(Number);
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);

    const continuousData = [];
    for (let year = minYear; year <= maxYear; year++) {
      continuousData.push({
        year: year,
        count: yearCounts[year] || 0,
        // Add a sequential index for animation
        index: year - minYear
      });
    }

    return continuousData.sort((a, b) => a.year - b.year);
  }, [data]);

  // Process institution data
  const institutionData = React.useMemo(() => {
    const institutionCounts = data.reduce((acc, item) => {
      const inst = item.institution;
      if (inst) {
        acc[inst] = (acc[inst] || 0) + 1;
      }
      return acc;
    }, {});

    return Object.entries(institutionCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [data]);

  // Process output type data
  const outputTypeData = React.useMemo(() => {
    const typeCounts = data.reduce((acc, item) => {
      const type = item.outputType;
      if (type) {
        acc[type] = (acc[type] || 0) + 1;
      }
      return acc;
    }, {});

    return Object.entries(typeCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value); // Sort by value in descending order
  }, [data]);
  // Calculate dynamic height for output types chart (60px per item, minimum 300px)
  const outputTypesChartHeight = Math.max(300, outputTypeData.length * 60);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border rounded shadow-lg">
          <p className="font-medium">{label}</p>
          <p className="text-sm text-gray-600">
            Count: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8">
      {/* Yearly Trend */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Research Output by Year</h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={yearlyData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis 
                dataKey="year"
                tick={{ fill: COLORS.primary.dark }}
                type="number"
                domain={['dataMin', 'dataMax']}
                tickCount={Math.min(yearlyData.length, 10)}
              />
              <YAxis 
                tick={{ fill: COLORS.primary.dark }}
                allowDecimals={false}
                domain={[0, 'auto']}
              />
              <Tooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-white p-2 border rounded shadow-lg">
                        <p className="font-medium">Year: {label}</p>
                        <p className="text-sm text-gray-600">
                          Outputs: {payload[0].value}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke={COLORS.primary.main} 
                strokeWidth={2}
                dot={{ 
                  fill: COLORS.primary.dark,
                  r: 4
                }}
                isAnimationActive={false}
                animationDuration={1000}
                animationBegin={0}
                strokeDasharray="0"
                onAnimationStart={() => console.log('Animation started')}
                onAnimationEnd={() => console.log('Animation ended')}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Institutions */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Top 10 Institutions</h3>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={institutionData}
              layout="vertical"
              margin={{ left: 200 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis 
                type="number"
                tick={{ fill: COLORS.primary.dark }}
              />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={180}
                tick={{ fontSize: 12, fill: COLORS.primary.dark }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                fill={COLORS.primary.main}
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Distribution of Output Types</h3>
        <div style={{ height: `${outputTypesChartHeight}px`, minHeight: '300px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={outputTypeData}
              layout="vertical"
              margin={{ left: 200, right: 30, top: 10, bottom: 10 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#eee" 
                horizontal={false}
              />
              <XAxis 
                type="number"
                tick={{ fill: COLORS.primary.dark }}
                tickLine={true}
              />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={180}
                tick={{ 
                  fill: COLORS.primary.dark,
                  fontSize: 12,
                  lineHeight: 16
                }}
                interval={0} // Force display all labels
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                fill={COLORS.primary.dark}
                radius={[0, 4, 4, 0]}
                barSize={30} // Adjust bar height
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 text-sm text-gray-500 text-right">
          Total Output Types: {outputTypeData.length}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsView;